import React, { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DAYS = 7;
const isStandalone = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true);

const isIOS = () =>
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !/crios|fxios/i.test(navigator.userAgent);

const InstallPWA = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        // Register Service Worker
        if ("serviceWorker" in navigator) {
            window.addEventListener("load", () => {
                navigator.serviceWorker.register("/sw.js").catch(() => null);
            });
        }

        if (isStandalone()) return; // already installed

        // Respect dismiss cooldown
        const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
        if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000) return;

        const onBefore = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setTimeout(() => setVisible(true), 4500); // wait a bit so it doesn't pop instantly
        };
        window.addEventListener("beforeinstallprompt", onBefore);

        // iOS fallback (Safari doesn't fire beforeinstallprompt)
        if (isIOS()) {
            setTimeout(() => { setIosHint(true); setVisible(true); }, 6000);
        }

        return () => window.removeEventListener("beforeinstallprompt", onBefore);
    }, []);

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try {
            await deferredPrompt.userChoice;
        } catch (e) { /* noop */ }
        setDeferredPrompt(null);
        setVisible(false);
    };

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm z-50 bg-white border-2 border-brand-ink rounded-sm shadow-2xl p-4 animate-in slide-in-from-bottom-4 duration-500"
        >
            <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                aria-label="Fechar"
                className="absolute top-2 right-2 w-7 h-7 inline-flex items-center justify-center hover:bg-brand-bg rounded-sm"
            >
                <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-brand-gold/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-brand-gold" />
                </div>
                <div className="flex-1 min-w-0 pr-6">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-brand-gold mb-0.5">Instale o app</div>
                    <div className="font-heading font-bold text-sm text-brand-ink leading-snug">
                        Tenha a loja sempre à mão
                    </div>
                    <p className="text-xs text-brand-inkSoft mt-1 leading-relaxed">
                        {iosHint
                            ? "No Safari, toque em compartilhar e depois em \"Adicionar à Tela de Início\"."
                            : "Instale na tela inicial — abertura instantânea, sem precisar abrir o navegador."}
                    </p>

                    {!iosHint && deferredPrompt && (
                        <button
                            onClick={install}
                            data-testid="pwa-install-btn"
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-brand-ink text-white hover:bg-brand-dark px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Instalar agora
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InstallPWA;
